import { Box, Checkbox, FormControlLabel } from "@material-ui/core";
import { Cell, Grid } from "@shoothill/core";
import { useObserver } from "mobx-react-lite";
import React from "react";
import styled from "styled-components";

import { DarwinSelect } from "Components/AutoComplete/DarwinSelect";
import { PrimaryButton } from "Components/Buttons/Buttons";
import { SolidDivider } from "Views/PurchaseOrder/Form/Views/Dividers";
import { OrderEditLineViewModel } from "./OrderEditLineViewModel";
import { DeleteModal } from "Components/Modal/DeleteModal";
import { DarwinDateSelect } from "Components/DateSelect/DarwinDateSelect";
import { PurchaseOrderViewModel } from "../../PurchaseOrderViewModel";

interface IProps {
    className?: string;
    viewModel: OrderEditLineViewModel;
    poViewModel: PurchaseOrderViewModel;
    isDisabled: boolean;
}

const OrderEditLineViewBase: React.FC<IProps> = (props) => {
    // #region Code Behind
    const [deleteOpen, setDeleteOpen] = React.useState(false);

    const COLUMNS8 = "1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr";
    const COLUMNS12 = "1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr";

    const getOptionLabel = (option: any | null | undefined) => {
        if (option) {
            if (option.displayName) {
                return option.displayName;
            }
        }

        return "";
    };

    //this.isModelValid

    const handleOpen = async () => {
        if (await props.viewModel.isModelValid()) {
            setDeleteOpen(true);
        }
    };

    const handleClose = () => {
        setDeleteOpen(false);
    };

    const handleEditLine = () => {
        props.viewModel.add();
        setDeleteOpen(false);
    };

    // #endregion Code Behind

    const renderNonStockOrderLine = () => {
        return (
            <OrderLinePanel key={"ORDERLINE-NONSTOCK"}>
                <OrderLinePanelSection>
                    <Grid columnGap={30} rowGap={20} tc={COLUMNS12} dc={COLUMNS12}>
                        <Cell ts={4} ds={4}>
                            <DarwinSelect
                                displayName="Category:*"
                                execute={props.viewModel.setCategoryAsync}
                                fullWidth={true}
                                getOptionLabel={(option: any) => getOptionLabel(option)}
                                onBlur={() => props.viewModel.isFieldValid("categoryId")}
                                options={props.viewModel.categoriesForIEAndVariations}
                                placeholder="Please select"
                                validationMessage={props.viewModel.getError("categoryId")}
                                value={props.viewModel.category}
                                canExecute={!props.isDisabled}
                            />
                        </Cell>
                        <Cell ts={4} ds={4}>
                            <DarwinSelect
                                displayName="Sub-category:*"
                                execute={props.viewModel.setSubCategoryAsync}
                                fullWidth={true}
                                getOptionLabel={(option: any) => getOptionLabel(option)}
                                onBlur={() => props.viewModel.isFieldValid("subCategoryId")}
                                options={props.viewModel.subCategoriesForCategory}
                                placeholder="Please select"
                                validationMessage={props.viewModel.getError("subCategoryId")}
                                value={props.viewModel.subCategory}
                                canExecute={!props.isDisabled}
                            />
                        </Cell>
                        <Cell ts={4} ds={4}>
                            <DarwinSelect
                                displayName="Line description:*"
                                execute={props.viewModel.setLineDescription}
                                fullWidth={true}
                                getOptionLabel={(option: any) => getOptionLabel(option)}
                                onBlur={() => props.viewModel.isFieldValid("lineDescriptionId")}
                                options={props.viewModel.lineDescriptionsForSubCategory}
                                placeholder="Please select"
                                validationMessage={props.viewModel.getError("lineDescriptionId")}
                                value={props.viewModel.lineDescription}
                                canExecute={!props.isDisabled}
                            />
                        </Cell>
                    </Grid>
                </OrderLinePanelSection>

                {props.poViewModel.canDisplayHireOrderTypeFeatures && !props.poViewModel.isHireLegacy && (
                    <OrderLinePanelSection>
                        <Grid columnGap={30} rowGap={20} tc={COLUMNS12} dc={COLUMNS12}>
                            <>
                                <Cell ts={2} ds={2}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={props.viewModel.model.isNA}
                                                onChange={(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => props.viewModel.setValue("isNA", checked)}
                                                disabled={props.poViewModel.IsLoading}
                                            />
                                        }
                                        label={"Hire period N/A"}
                                        name={`is-na-checkbox`}
                                        disabled={props.poViewModel.IsLoading}
                                        labelPlacement={"top"}
                                        style={{ marginBottom: "-10px" }}
                                    />
                                </Cell>
                                {!props.viewModel.model.isNA && (
                                    <>
                                        <Cell ts={2} ds={2}>
                                            <DarwinDateSelect
                                                displayName="Date from:*"
                                                execute={(value: string | null) => props.viewModel.setValue("dateFrom", value)}
                                                onBlur={() => props.viewModel.isFieldValid("dateFrom")}
                                                placeholder="Please select"
                                                validationMessage={props.viewModel.getError("dateFrom")}
                                                value={props.viewModel.getValue("dateFrom")}
                                                clearable
                                                //canExecute={props.viewModel.canExecuteHireOrderDateField}
                                            />
                                        </Cell>

                                        <Cell ts={2} ds={2}>
                                            <DarwinDateSelect
                                                displayName="Date to:*"
                                                execute={(value: string | null) => props.viewModel.setValue("dateTo", value)}
                                                onBlur={() => props.viewModel.isFieldValid("dateTo")}
                                                placeholder="Please select"
                                                validationMessage={props.viewModel.getError("dateTo")}
                                                value={props.viewModel.getValue("dateTo")}
                                                clearable
                                                //canExecute={props.viewModel.canExecuteHireOrderDateField}
                                            />
                                        </Cell>
                                    </>
                                )}
                            </>
                        </Grid>
                    </OrderLinePanelSection>
                )}
                <SolidDivider />
            </OrderLinePanel>
        );
    };

    return useObserver(() => (
        <Grid className={props.className} columnGap={30} rowGap={20} tc={COLUMNS8} dc={COLUMNS8}>
            <Cell ts={8} ds={8}>
                {(() => {
                    switch (props.viewModel.isStockOrderLine) {
                        case false:
                            return renderNonStockOrderLine();
                    }
                })()}
            </Cell>
            <Cell ts={1} ds={1}>
                <PrimaryButton displayName="Edit line" execute={() => handleOpen()} fullWidth={true} canExecute={!props.isDisabled && props.viewModel.lineDescription != null} />
            </Cell>
            <DeleteModal
                open={deleteOpen}
                onClose={handleClose}
                onDelete={handleEditLine}
                title={`Edit line?`}
                text={`Are you sure you want change the line on this purchase order?`}
            />
        </Grid>
    ));
};

export const OrderLinePanel = styled(Box)`
    background-color: #ededed;
    display: flex;
    flex-direction: column;
`;

export const OrderLinePanelSection = styled(Box)`
    margin: 18px 16px;
`;

export const OrderEditLineView = styled(OrderEditLineViewBase)``;

export const OrderLineUnitTotal = styled(Box)`
    display: flex;
    align-items: end;
    margin-bottom: 6px;
    font-size: 12px;
    font-weight: bold;
`;
